/**
 * WEB-3447 — ОДНА дверь ленивого куска плеера покупателя, которым воронка
 * пользуется не всегда: вычисляемые значения роста и веса (`{{bmi}}` и др.,
 * `computedValuesDoor.ts`) и рамка экрана эталона Slimkit
 * (`quiz-runtime/quizChromeKitDoor.ts`). Решение владельца 16.10.2026 по
 * WEB-3128: «ленивые куски», потолок веса первого экрана не поднимать.
 *
 * Приём тот же, что у стиль-пака (`themes/stylePacks.ts`, `useStylePackDoor`)
 * и кусков рендерера (`publicBlockChunkLoaders.ts`):
 *  - кусок читается СИНХРОННО (`get`) — рендер и подстановка переменных не
 *    становятся асинхронными;
 *  - ЖДЁТ страница (`useDoor(needed)` до первого кадра): воронка, которой кусок
 *    нужен, не рисуется, пока его нет, — пустой `{{bmi}}` или шапка без рамки
 *    покупателю не мелькнут ни кадром;
 *  - кабинет и тестовая обвязка кладут кусок статически (`register`,
 *    `setSyncResolver`) — превью конструктора рисуется тем же кадром.
 *
 * Загрузка — через `importer` зовущего; он сам оборачивает `import()` в
 * `retryImport` с ЛИТЕРАЛЬНЫМ ключом защёлки в месте вызова (одна перезагрузка
 * за свежим `index.html`, если выкат унёс файл) — так требует сторож ключей
 * `utils/__tests__/lazyWithRetry.callKeys.guard.test.ts` (ключ-шаблон здесь
 * его краснил, возврат 1 захода 3 WEB-3447). Отказ НЕ бросает: дверь перестаёт ждать, и воронка
 * рисуется без куска (как пак по умолчанию при отказе пака) — вечного
 * прелоадера нет.
 *
 * Сам кусок фабрика тянет только через переданный `importer` — динамический
 * `import()` у зовущего. Статический импорт куска мимо двери вернул бы его в
 * первый экран; это краснит `FIRST_SCREEN_DEFERRED_MODULES` сторожа сборки.
 */
import { useEffect, useState } from 'react';

export interface LazyModuleDoor<M> {
  /** Кусок на руках (или его отдал синхронный добытчик) — иначе null. */
  get(): M | null;
  /** Ждать нечего: кусок приехал, положен статически или его заказ отказал. */
  hasSettled(): boolean;
  /** Привезти кусок. Не бросает; два заказа — один запрос. */
  load(): Promise<void>;
  /** Положить кусок статически (кабинет). */
  register(module: M): void;
  /** Шов тестовой обвязки: синхронный добытчик (см. jest.setup.ts). */
  setSyncResolver(resolver: (() => M | null) | null): void;
  getSyncResolver(): (() => M | null) | null;
  /**
   * Дверь страницы: `needed` — воронке кусок нужен. Ждать — только пока
   * нужен и заказ не закончился; выводится из реестра на каждой отрисовке.
   */
  useDoor(needed: boolean): { readonly waiting: boolean };
  /** Только для тестов: «ничего не качали», добытчик снят. */
  reset(): void;
}

export function createLazyModuleDoor<M>(
  importer: () => Promise<M>,
): LazyModuleDoor<M> {
  let loaded: M | null = null;
  let failed = false;
  let inFlight: Promise<void> | null = null;
  let syncResolver: (() => M | null) | null = null;

  const get = (): M | null => {
    if (loaded) return loaded;
    const resolved = syncResolver ? syncResolver() : null;
    if (resolved) loaded = resolved;
    return resolved ?? null;
  };

  const hasSettled = (): boolean => get() !== null || failed;

  const load = (): Promise<void> => {
    if (hasSettled()) return Promise.resolve();
    if (inFlight) return inFlight;
    const request = importer()
      .then((module) => {
        loaded = module;
      })
      .catch(() => {
        failed = true;
      })
      .finally(() => {
        inFlight = null;
      });
    inFlight = request;
    return request;
  };

  function useDoor(needed: boolean): { readonly waiting: boolean } {
    const [, setArrived] = useState(0);
    const waiting = needed && !hasSettled();
    useEffect(() => {
      if (!waiting) return;
      let alive = true;
      void load().then(() => {
        if (alive) setArrived((n) => n + 1);
      });
      return () => {
        alive = false;
      };
    }, [waiting]);
    return { waiting };
  }

  return {
    get,
    hasSettled,
    load,
    register(module: M) {
      loaded = module;
    },
    setSyncResolver(resolver) {
      syncResolver = resolver;
    },
    getSyncResolver: () => syncResolver,
    useDoor,
    reset() {
      loaded = null;
      failed = false;
      inFlight = null;
      syncResolver = null;
    },
  };
}
